import { substituteProjectTags } from './substituteProjectTags.js';
import { addCommas } from '../utils/numberUtils.ts';
import { removeUTMParams } from '../adapters/dupes/queryParams.js';
import { decodeXml } from '../utils/encodeXML.ts';

// Formatting code previously shared between React & Handlebars project templates
export const projectFormatter = {};

projectFormatter.parseDate = function (dateString) {
	// Some callers will pass epoch ms instead of an actual date string
	if (typeof dateString === "number") {
		return new Date(dateString);
	}

	var expArray = dateString.split("-");
	var year = parseInt(expArray[0], 10);
	var month = parseInt(expArray[1], 10) - 1;
	var date = parseInt(expArray[2], 10);
	return new Date(year, month, date);
};

projectFormatter.expiresSoon = function(expirationDate) {
	var threshold = new Date((new Date()).getTime() + 10 * 24 * 60 * 60 * 1000);
	var exp = projectFormatter.parseDate(expirationDate);
	return threshold.getTime() > exp.getTime();
};

projectFormatter.daysLeft = function(expirationDate) {
	// check for time zone
	var now = new Date();
	var exp = projectFormatter.parseDate(expirationDate);

	if (now.getTime() < exp.getTime()) {
		var timeLeft = exp.getTime() - now.getTime();
		if (timeLeft > 24*60*60*1000) {
			var daysLeft = Math.floor(timeLeft / (24*60*60*1000));
			return "<span>" + daysLeft + (daysLeft > 1 ? "</span> days left!" : "</span> day left!");
		} else {
			var hoursLeft = Math.floor(timeLeft / (60*60*1000));
			if (hoursLeft > 1) return "<span>" + hoursLeft + "</span> hours left!";
			else return "<span class='minutes-left'>minutes left!</span>";
		}
	} else {
		return "";
	}
};

/**
 * @param {string} fullURL Full project URL
 * @param {number=} creatorId ID of the project creator
 * @return {string} Project URL with UTM params removed and rf param added if applicable
 */
projectFormatter.removeUTMParams = function(fullURL, creatorId) {
	var fixedURL = removeUTMParams(decodeXml(fullURL));
	if (isTeacherUserPage() || isCurrentUser(creatorId)) {
		var rebuildRFParam = getDefaultRFParamValue(fixedURL, "link", null, creatorId);
		fixedURL = addTeacherChallengeIdParamBasedOnRFParam(setURLParam(fixedURL,"rf", rebuildRFParam));
		if (typeof teacherChallengeId != "undefined") {
			fixedURL = setURLParam(fixedURL,"challengeid", teacherChallengeId);
		}
	}

	return fixedURL;
};

projectFormatter.daysAgo = function(modifiedDate) {
	// check for time zone
	//Sat Jul 14 04:00:00 PDT 2012
	var now = new Date();
	var mod = new Date(modifiedDate);
	var daysAgo = now.getTime() - mod.getTime();

	if (daysAgo > 7*24*60*60*1000) {
		return "on " + formatMediumDate(mod);
	} else if (daysAgo > 24*60*60*1000) {
		var days = Math.round(daysAgo / (24*60*60*1000));
		if (days > 1) return days + " days ago";
		else return "1 day ago";
	} else if (daysAgo > 60*60*1000) {
		var hours = Math.round(daysAgo / (60*60*1000));
		if (hours > 1) return hours + " hours ago";
		else return "1 hour ago";
	} else {
		return "minutes ago";
	}
};

projectFormatter.formatMyStudentsNeed = function(project) {
	if (project.fulfillmentTrailer) {
		// Replace double quotes with single quotes since this is already a teacher quote
		// Also, strip anchor tags since the whole project card is already a link
		var trailer = project.fulfillmentTrailer.replace(/<\/?a( [^>]*)?>/g, "").replace(/"/g, "'");

		if (project.studentLed) {
			trailer = trailer.replace(/^My students need/, "We need");
			trailer = trailer.replace(/^Help me give my students/, "Help us get");
		}
		return substituteProjectTags(trailer, "search");
	} else {
		return "";
	}
};

projectFormatter.lightboxURL = function (proposalURL, index) {
	// This code assumes our S3 bucket is still named donorschoose-storage
	var newURL = removeUTMParams(decodeXml(proposalURL));
	if (typeof index != "undefined") newURL = setURLParam(newURL, "lightbox", "photoIndex" + index);
	return newURL;
};

projectFormatter.lightboxImage = function (lightboxItem) {
	if (lightboxItem.hasOwnProperty("smallPhotoURL") && lightboxItem.smallPhotoURL.indexOf("small/") > -1) {
		return lightboxItem.smallPhotoURL;
	}
	else if (lightboxItem.indexOf("small/") > -1) {
		return lightboxItem;
	}
	return "";
};

projectFormatter.formatCurrency = function(amount) {
	var dollars = parseInt(Math.ceil(parseFloat(amount)));
	return "$" + addCommas(dollars);
};

/**
 * @param amount The cost to complete
 * @param totalAmount The total price
 * @param matchAmount The amount provided by the matching fund
 * @returns {number} The fraction of the project's cost that has been provided by donations (not a matching fund)
 */
projectFormatter.fractionFunded = function(amount, totalAmount, matchAmount) {
	return ((parseFloat(totalAmount) - (parseFloat(amount) + parseFloat(matchAmount))) / parseFloat(totalAmount));
};

/**
 * @param amount The amount the matching fund is providing
 * @param totalAmount the total price
 * @returns {number} The fraction of the project cost covered by a matching fund
 */
projectFormatter.fractionMatched = function(amount, totalAmount) {
	return (parseFloat(amount) / parseFloat(totalAmount));
};
